import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Loader2,
  Wifi,
  ArrowRight,
  FileText
} from 'lucide-react';

// Legal document URLs
const LEGAL_DOCS = {
  agb: "https://customer-assets.emergentagent.com/job_nexiu-vertrieb/artifacts/2ohnywsm_20240223_AGB_nexiu_GmbH.pdf",
  datenschutzerklaerung: "https://customer-assets.emergentagent.com/job_nexiu-vertrieb/artifacts/navdbf5n_Datenschutzerklaerung.pdf",
  datenschutzhinweis: "https://customer-assets.emergentagent.com/job_nexiu-vertrieb/artifacts/8tqd865j_Datenschutzhinweis.pdf",
  pib: "https://customer-assets.emergentagent.com/job_nexiu-vertrieb/artifacts/o75d74ho_pib-pk-festnetz-nexiu-SafeConnect-300_900.pdf",
  vorvertragliche: "https://customer-assets.emergentagent.com/job_nexiu-vertrieb/artifacts/60mzzd5b_Vorvertragliche-Informationen.pdf",
  widerrufsrecht: "https://customer-assets.emergentagent.com/job_nexiu-vertrieb/artifacts/w96os8jz_Widerrufsrecht.pdf"
};

const CustomerConfirm = () => {
  const { token } = useParams();
  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const loadOrderInfo = useCallback(async () => {
    try {
      const response = await api.getConfirmationInfo(token);
      setOrderInfo(response.data);
      if (response.data.status === 'already_confirmed') {
        setConfirmed(true);
      }
    } catch (error) {
      setOrderInfo({ status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadOrderInfo();
  }, [loadOrderInfo]);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await api.confirmOrder(token);
      setConfirmed(true);
      toast.success('Bestellung erfolgreich bestätigt!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bestätigung fehlgeschlagen');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F47920]"></div>
      </div>
    );
  }

  // Error state
  if (orderInfo?.status === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl text-center">
          <CardContent className="p-8">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bestellung nicht gefunden
            </h1>
            <p className="text-slate-600">
              Der Bestätigungslink ist ungültig oder abgelaufen.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Cancelled state
  if (orderInfo?.status === 'cancelled') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl text-center">
          <CardContent className="p-8">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-slate-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bestellung storniert
            </h1>
            <p className="text-slate-600">
              Die Bestellung #{orderInfo.order_number} wurde storniert und kann nicht mehr bestätigt werden.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already confirmed state
  if (confirmed || orderInfo?.status === 'already_confirmed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Vielen Dank!
            </h1>
            <p className="text-slate-600 mb-6">
              Ihre Bestellung #{orderInfo?.order_number} wurde erfolgreich bestätigt.
            </p>
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500">
                Wir werden uns in Kürze mit Ihnen in Verbindung setzen, um einen Schalttermin zu vereinbaren.
              </p>
            </div>
            <a 
              href="https://nexiu.de" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#F47920] hover:underline mt-6"
            >
              Zur nexiu Website
              <ArrowRight className="w-4 h-4" />
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pending confirmation
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F47920]/5 to-white">
      {/* Header */}
      <div className="bg-[#F47920] text-white py-8 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
            <Wifi className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            nexiu SafeConnect
          </h1>
          <p className="text-white/80">Bestellbestätigung</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto p-4 -mt-8">
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Hallo {orderInfo?.customer_name}!
            </CardTitle>
            <p className="text-slate-600 mt-2">
              Bitte bestätigen Sie Ihre Bestellung, um den Auftrag abzuschließen.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Order Summary */}
            <div className="p-6 bg-slate-50 rounded-xl space-y-4">
              <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                <span className="text-slate-600">Bestellnummer</span>
                <span className="font-semibold text-lg">#{orderInfo?.order_number}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Produkt</span>
                <span className="font-medium">{orderInfo?.product_name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Monatliche Kosten</span>
                <span className="font-semibold text-lg text-[#F47920]">
                  {formatCurrency(orderInfo?.monthly_total)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Einmalige Kosten</span>
                <span className="font-semibold">{formatCurrency(orderInfo?.setup_fee)}</span>
              </div>
            </div>

            {/* Legal Documents */}
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
              <p className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#F47920]" />
                Vertragsunterlagen
              </p>
              <div className="space-y-2 text-sm">
                <a href={LEGAL_DOCS.agb} target="_blank" rel="noopener noreferrer" className="block text-[#F47920] hover:underline">
                  → Allgemeine Geschäftsbedingungen (AGB)
                </a>
                <a href={LEGAL_DOCS.pib} target="_blank" rel="noopener noreferrer" className="block text-[#F47920] hover:underline">
                  → Produktinformationsblatt
                </a>
                <a href={api.getVorvertraglicheInfoPublicUrl(token)} target="_blank" rel="noopener noreferrer" className="block text-[#F47920] hover:underline font-medium">
                  → Vorvertragliche Informationen (Ihre Bestellung)
                </a>
                <a href={LEGAL_DOCS.widerrufsrecht} target="_blank" rel="noopener noreferrer" className="block text-[#F47920] hover:underline">
                  → Widerrufsrecht & Widerrufsformular
                </a>
                <a href={LEGAL_DOCS.datenschutzerklaerung} target="_blank" rel="noopener noreferrer" className="block text-[#F47920] hover:underline">
                  → Datenschutzerklärung
                </a>
              </div>
            </div>

            {/* Legal notice */}
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
              <p className="text-sm text-amber-800">
                <strong>Hinweis:</strong> Mit der Bestätigung erteilen Sie einen verbindlichen Auftrag. 
                Die Widerrufsfrist beginnt mit Erhalt der Vertragsunterlagen.
              </p>
            </div>

            {/* Confirm Button */}
            <Button
              onClick={handleConfirm}
              disabled={confirming}
              className="w-full h-14 text-lg bg-[#F47920] hover:bg-[#E06810]"
              data-testid="confirm-order-btn"
            >
              {confirming ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Wird bestätigt...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Bestellung bestätigen
                </>
              )}
            </Button>

            <p className="text-center text-sm text-slate-500">
              Bei Fragen wenden Sie sich an{' '}
              <a href="mailto:info@nexiu.de" className="text-[#F47920] hover:underline">
                info@nexiu.de
              </a>
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>nexiu GmbH - Ihr zuverlässiger Internetanbieter</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="https://nexiu.de/nexiu-kundenservice/impressum/" target="_blank" rel="noopener noreferrer" className="hover:text-[#F47920]">
              Impressum
            </a>
            <a href="https://nexiu.de/nexiu-kundenservice/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer" className="hover:text-[#F47920]">
              Datenschutz
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerConfirm;
